import React from "react";
import { Form } from "react-crud-form";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Input } from "ui/components/input";
import { FormLabel } from "components/form";

import { Textarea } from "ui/components/textarea";
import { Label } from "ui/components/label";
import { useNavigate } from "react-router-dom";
import { companySchema } from "./schema";
import { useCompanyForm } from "./useCompanyForm";
import { FormColumns } from "components/form";
import { FormActions } from "components/form/actions";
import { CountryCombobox } from "@/modules/crm/clients/components/directus-combobox";
import { PermissionControlledInput } from "permissions/components/input";

const CompanyForm = ({ companyId }) => {
  const navigate = useNavigate();
  const { form, requiredFields, handleCreate, handleUpdate, handleRead } =
    useCompanyForm();

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={companyId || "+"}
      schema={companySchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => (
        <div className="space-y-6">
          {/* Name Field */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="companies"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Name
                  </FormLabel>
                  <Input {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Website Field */}
          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="companies"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Website
                  </FormLabel>
                  <Input type="url" {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Mobile Number Field */}
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="companies"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Mobile Number
                  </FormLabel>
                  <Input {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Email Field */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="companies"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Email
                  </FormLabel>
                  <Input type="email" {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Tax Number Field */}
          <FormField
            control={form.control}
            name="tax_number"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="companies"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Tax Number
                  </FormLabel>
                  <Input {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Address Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Address Information</h3>

            {/* Address Line 1 */}
            <FormField
              control={form.control}
              name="address_line_1"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="companies"
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Address Line 1
                    </FormLabel>
                    <Input {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address Line 2 */}
            <FormField
              control={form.control}
              name="address_line_2"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="companies"
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Address Line 2
                    </FormLabel>
                    <Input {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* City, State, Zipcode Row */}

            <FormColumns>
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:*"}
                      collection="companies"
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        City
                      </FormLabel>
                      <Input {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:*"}
                      collection="companies"
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        State/Province
                      </FormLabel>
                      <Input {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormColumns>
              <FormField
                control={form.control}
                name="zipcode"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:*"}
                      collection="companies"
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Zip/Postal Code
                      </FormLabel>
                      <Input {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Country Field */}
              <FormField
                control={form.control}
                name="country_id"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:*"}
                      collection="companies"
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Country
                      </FormLabel>
                      <CountryCombobox
                        value={field.value}
                        onChange={field.onChange}
                        searchPlaceholder="Search countries..."
                        // addButton={{
                        //   label: "Add New Country",
                        //   onClick: () => navigate("/countries/+"),
                        // }}
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>
          </div>

          <FormActions
            isNewItem={isNewItem}
            isCreating={isCreating}
            isUpdating={isUpdating}
            isDirty={isDirty}
            createItem={() => createItem().then(() => navigate(-1))}
            updateItem={() => updateItem().then(() => navigate(-1))}
            showCancel={false}
            collection="companies"
            rule={isNewItem ? "create:*" : "update:*"}
          />
        </div>
      )}
    </Form>
  );
};

export default CompanyForm;
